import { useCallback, useMemo } from 'react'
import { Button, Empty, Pagination, Spin, Tabs } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { List, useTable } from '@refinedev/antd'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { SalesHistoryDateGroupRoot } from '@repo/sdk'

import { useSyncTableWithUrl } from '../../shared/hooks/useSyncTableWithUrl'
import { useFoDisplayStatusConfig } from './foDisplayStatusConfig'
import { useOrderTabCounts } from './useOrderTabCounts'
import { useOrderListUrlSync } from './useOrderListUrlSync'
import { DateGroupCollapse } from './DateGroupCollapse'
import { isDisplayStatus } from './orderDisplayStatuses'
import { createOrderTabItems } from './createOrderTabItems'
import { BuyerNewOrderTable } from './BuyerNewOrderTable'
import {
  groupFulfillmentOrdersByBuyer,
  type BuyerNewOrderGroup,
} from './groupFulfillmentOrdersByBuyer'

export function OrderList() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const statusConfig = useFoDisplayStatusConfig()
  const { counts } = useOrderTabCounts()
  const { activeTab, currentPage: urlPage, setTab, setPage } = useOrderListUrlSync()

  const { tableQuery, setFilters, currentPage, setCurrentPage, pageSize, result } =
    useTable<SalesHistoryDateGroupRoot>({
      resource: 'order-list',
      pagination: { pageSize: 20, currentPage: urlPage },
      syncWithLocation: false,
      filters: {
        initial: [{ field: 'displayStatus', operator: 'eq', value: activeTab }],
      },
      // 신규주문 (PAID) and the other tabs render the same payload differently, so keeping the
      // previous tab's rows on screen while the new tab loads shows the wrong list.
      queryOptions: { placeholderData: undefined },
    })

  const filters = useMemo(
    () => [{ field: 'displayStatus', operator: 'eq' as const, value: activeTab }],
    [activeTab],
  )
  useSyncTableWithUrl({ filters, urlPage, currentPage, setFilters, setCurrentPage })

  const dateGroups = result.data
  const isFetching = tableQuery.isFetching
  const total = result.total ?? 0
  const buyerNewOrderGroups = useMemo(
    () =>
      groupFulfillmentOrdersByBuyer(
        dateGroups.flatMap((dateGroup) =>
          dateGroup.broadcastGroups.flatMap((broadcastGroup) => broadcastGroup.fulfillmentOrders),
        ),
      ),
    [dateGroups],
  )

  const handleTabChange = useCallback(
    (key: string) => {
      if (!isDisplayStatus(key)) return
      setTab(key)
    },
    [setTab],
  )

  const handleViewDetail = useCallback((id: string) => void navigate(`/orders/${id}`), [navigate])
  // The clicked group carries its own anchor. Looking the group up by buyer id instead sent a
  // buyer's second parcel to the first parcel's detail, because a buyer shipping to two addresses
  // now has two rows and `find` returned whichever came first.
  const handleViewBuyerDetail = useCallback(
    (group: BuyerNewOrderGroup) => {
      const anchorId = group.fulfillmentOrders[0]?.fulfillmentOrderId
      if (anchorId) void navigate(`/orders/${anchorId}?buyerId=${group.buyerId}`)
    },
    [navigate],
  )

  // Re-setting identical filters does not change refine's content-hashed query key,
  // so the old "refresh" never refetched anything.
  const handleRefresh = useCallback(() => void tableQuery.refetch(), [tableQuery])

  const tabItems = useMemo(() => createOrderTabItems(statusConfig, counts), [statusConfig, counts])
  return (
    <List
      title={t('pages.orders.listTitle')}
      headerButtons={
        <Button
          data-testid="order-list-refresh-btn"
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
        >
          {t('common.buttons.refresh')}
        </Button>
      }
    >
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
      <Spin spinning={isFetching}>
        {dateGroups.length > 0 ? (
          activeTab === 'PAID' ? (
            <BuyerNewOrderTable
              groups={buyerNewOrderGroups}
              onViewDetail={handleViewBuyerDetail}
              t={t}
            />
          ) : (
            <DateGroupCollapse
              dateGroups={dateGroups}
              onViewDetail={handleViewDetail}
              t={t}
              statusConfig={statusConfig}
            />
          )
        ) : (
          !isFetching && (
            <Empty
              description={t('pages.orders.emptyState')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        )}
      </Spin>
      {total > pageSize && (
        <Pagination
          current={urlPage}
          pageSize={pageSize}
          total={total}
          onChange={(page) => setPage(page)}
          showSizeChanger={false}
          style={{ marginTop: 16, textAlign: 'right' }}
        />
      )}
    </List>
  )
}
